import { lazy, Suspense, type ReactNode } from 'react';
import {
  HashRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Layout } from './components/Layout.js';
import { Spinner } from './components/ui.js';
import { AuthProvider, useAuth } from './state/auth.js';
import { ToastProvider } from './state/toast.js';
import { LoginPage, RegisterPage } from './pages/AuthPages.js';

/**
 * Route-level code splitting.
 *
 * ## Why, and what it buys
 *
 * The console has twelve pages and a reader visits one of them. Without splitting,
 * the sign-in form ships the audit log, the API key manager and the request
 * builder — a single 425 kB bundle for someone who is about to type a password
 * and leave. Splitting puts the shell and the auth flow in the entry chunk and
 * each page in its own, so the first paint depends only on what the first paint
 * needs.
 *
 * The shell — React, the router, the design system, the API client — stays in the
 * entry chunk because every page needs it. That is the part worth caching, and it
 * is also the part that changes least.
 *
 * ## Why the imports are written out rather than generated
 *
 * A helper that took a module path and a named export would need either a cast or
 * a conditional type, and both would cost more type safety than the twelve lines
 * they save. Written out, a renamed export is a compile error at the exact line
 * that needs fixing.
 */
const AcceptInvitationPage = lazy(async () => ({
  default: (await import('./pages/InvitationPages.js')).AcceptInvitationPage,
}));
const CreateOrganizationPage = lazy(async () => ({
  default: (await import('./pages/InvitationPages.js')).CreateOrganizationPage,
}));
const OverviewPage = lazy(async () => ({
  default: (await import('./pages/OverviewPage.js')).OverviewPage,
}));
const MembersPage = lazy(async () => ({
  default: (await import('./pages/MembersPage.js')).MembersPage,
}));
const InvitationsPage = lazy(async () => ({
  default: (await import('./pages/InvitationsPage.js')).InvitationsPage,
}));
const ApiKeysPage = lazy(async () => ({
  default: (await import('./pages/ApiKeysPage.js')).ApiKeysPage,
}));
const SessionsPage = lazy(async () => ({
  default: (await import('./pages/SessionsPage.js')).SessionsPage,
}));
const AuditPage = lazy(async () => ({ default: (await import('./pages/AuditPage.js')).AuditPage }));
const SettingsPage = lazy(async () => ({
  default: (await import('./pages/SettingsPage.js')).SettingsPage,
}));
const TryItPage = lazy(async () => ({ default: (await import('./pages/TryItPage.js')).TryItPage }));
const NotFoundPage = lazy(async () => ({
  default: (await import('./pages/NotFoundPage.js')).NotFoundPage,
}));

/**
 * The route tree.
 *
 * ## Why `HashRouter` and not `BrowserRouter`
 *
 * The API builds invitation links as `${appOrigin}/#/invitations/<token>` — see
 * `apps/api/src/routes/invitations.ts`. That is the contract, and a browser router
 * would produce links the API does not generate.
 *
 * It is also the right choice on its own merits. The console is static files on a
 * CDN, and a browser router needs the host to rewrite every unknown path to
 * `index.html`. Miss that rewrite — a new host, a changed `_redirects` file — and
 * every deep link becomes a 404 that only appears in production. A hash router
 * needs no server configuration at all, and the cost is a `#` in the URL.
 *
 * ## Why the providers are nested in this order
 *
 * `ToastProvider` is outside `AuthProvider` because the auth provider does not
 * raise toasts — it returns failures to its callers, which is the only way the
 * login form can render a field-level error. The toast provider is there for the
 * pages, which are all inside both.
 *
 * ## The two Suspense boundaries
 *
 * The outer one catches the three public pages, which are not inside the shell.
 * The inner one, in `Layout`, keeps the sidebar and header mounted while an
 * authenticated page loads — so navigating between two organisation pages does
 * not blank the whole screen and then redraw it.
 */
export function App(): ReactNode {
  return (
    <HashRouter>
      <ToastProvider>
        <AuthProvider>
          <Suspense fallback={<BootSplash />}>
            <Routes>
              {/* Public -------------------------------------------------- */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/invite/:token"
                element={
                  <Suspense fallback={<BootSplash />}>
                    <AcceptInvitationPage />
                  </Suspense>
                }
              />

              {/* Authenticated ------------------------------------------- */}
              <Route element={<RequireAuth />}>
                <Route element={<Layout />}>
                  <Route index element={<RootRedirect />} />
                  <Route path="/orgs/new" element={<CreateOrganizationPage />} />

                  <Route path="/orgs/:slug" element={<OverviewPage />} />
                  <Route path="/orgs/:slug/members" element={<MembersPage />} />
                  <Route path="/orgs/:slug/invitations" element={<InvitationsPage />} />
                  <Route path="/orgs/:slug/api-keys" element={<ApiKeysPage />} />
                  <Route path="/orgs/:slug/audit" element={<AuditPage />} />
                  <Route path="/orgs/:slug/settings" element={<SettingsPage />} />

                  <Route path="/sessions" element={<SessionsPage />} />
                  <Route path="/try" element={<TryItPage />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </ToastProvider>
    </HashRouter>
  );
}

/* -------------------------------------------------------------------------- */
/*  Guards                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Requires a session.
 *
 * The `loading` branch is the reason this is not a one-line component. The
 * provider starts in `loading` while it tries the silent refresh, and treating
 * that as "anonymous" would redirect to `/login` for one frame on every reload —
 * which a signed-in user would see as being thrown at a login form and then
 * bounced back.
 *
 * The intended destination is carried in `state.from`, so a deep link survives
 * the sign-in.
 */
function RequireAuth(): ReactNode {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <BootSplash />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return <Outlet />;
}

/**
 * Sends a signed-in caller somewhere real.
 *
 * `/` has no page of its own: the console is always scoped to an organisation, so
 * the landing page is whichever one the caller belongs to. An account with none
 * goes to the create flow rather than to an empty dashboard.
 */
function RootRedirect(): ReactNode {
  const { organizations } = useAuth();
  const first = organizations[0];
  return <Navigate to={first ? `/orgs/${first.slug}` : '/orgs/new'} replace />;
}

/**
 * The splash shown while the silent refresh is in flight, and while a page chunk
 * is being fetched.
 *
 * Deliberately minimal and fast: it is on screen for one round trip to
 * `/api/auth/refresh`, and a skeleton of a dashboard that then turns out to be a
 * login form is worse than a spinner that tells the truth.
 */
function BootSplash(): ReactNode {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-canvas">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex size-8 items-center justify-center rounded-lg bg-brand text-base font-bold text-brand-ink"
        >
          K
        </span>
        <span className="text-base font-semibold tracking-tight text-ink">Keystone</span>
      </div>
      <p className="flex items-center gap-2 text-[0.8125rem] text-ink-muted">
        <Spinner size="sm" label="Restoring your session" />
        <span>Restoring your session…</span>
      </p>
    </div>
  );
}
